import React, { useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import AlbumDetail from "./AlbumDetail";
import { useAlbum } from "../hooks/useAlbumsQuery";
import { LoadingSpinner } from "./shared";
import { albumKeys } from "../hooks/useAlbumsQuery";
import { albumService } from "../services/albumService";
import { authService } from "../services/authService";
import { Album } from "../types/album";

const AlbumPage: React.FC = () => {
  const { albumId, ownerId } = useParams<{ albumId: string; ownerId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const lastMarkedAlbumKeyRef = useRef<string | null>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [ownerId, albumId]);

  // Always fetch the album using the ownerId from the URL
  const { album, isLoading, error } = useAlbum(ownerId, albumId);

  useEffect(() => {
    if (!album || authService.isAnonymousUser()) {
      return;
    }

    const albumKey = `${album.ownerId}:${album.id}`;
    if (lastMarkedAlbumKeyRef.current === albumKey) {
      return;
    }
    lastMarkedAlbumKeyRef.current = albumKey;

    let isCancelled = false;

    const markAlbumAsAccessed = async () => {
      try {
        await albumService.markAlbumAsAccessed(album.ownerId, album.id);

        if (isCancelled) {
          return;
        }

        queryClient.setQueryData<Album | undefined>(
          albumKeys.detail(album.ownerId, album.id),
          (existingAlbum) =>
            existingAlbum
              ? { ...existingAlbum, hasNewMediaUploads: false }
              : existingAlbum,
        );
        queryClient.setQueryData<Album[] | undefined>(
          albumKeys.list(album.ownerId),
          (existingAlbums) =>
            existingAlbums?.map((existingAlbum) =>
              existingAlbum.id === album.id
                ? { ...existingAlbum, hasNewMediaUploads: false }
                : existingAlbum,
            ),
        );
      } catch (markAccessError) {
        console.error("Failed to mark album as accessed:", markAccessError);
      }
    };

    void markAlbumAsAccessed();

    return () => {
      isCancelled = true;
    };
  }, [album?.id, album?.ownerId, queryClient, album]);

  // Show loading while album is being fetched
  if (isLoading) {
    return (
      <div data-section="album-page-root"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <LoadingSpinner message="Loading album..." size="large" />
      </div>
    );
  }

  // Show error if there's an error or album not found
  if (error || !album) {
    return (
      <div data-section="album-page-root"
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: "#1e293b",
        }}
      >
        <div style={{ textAlign: "center", color: "white" }}>
          <h2 style={{ fontSize: "2rem", marginBottom: "1rem" }}>
            {error || "Album not found"}
          </h2>
          <p style={{ color: "#94a3b8", marginBottom: "2rem" }}>
            {error || "The album you're looking for doesn't exist or you don't have access to it."}
          </p>
          <button
            onClick={() => navigate("/")}
            style={{
              padding: "0.75rem 1.5rem",
              backgroundColor: "#7c3aed",
              color: "white",
              border: "none",
              borderRadius: "0.5rem",
              cursor: "pointer",
              fontSize: "1rem",
              fontWeight: "500",
            }}
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return <AlbumDetail album={album} />;
};

export default AlbumPage;